import React, { useState, useEffect, useRef } from 'react'
import QuizContent from '../shared/QuizContent'
import LogoIcon from '../../assets/Logo.svg'
import { handleLoginFlow, getGoogleAuthUrl, collectAndStoreUTMData, handleAuthCallback, getExternalClickIds, submitEmailOnly, createModelParams, LP_SOURCE } from '../../utils/api'
import { buildOAuthStatePath } from '../../utils/paywall'
import { getEfTransactionId } from '../../utils/everflow'
import { getAuthToken } from '../../utils/storage'
import { useQuizSelections } from '../../hooks/useLocalStorage' // Add this import
import { useFormValidation, validationRules } from '../../hooks/useFormValidation'
import { useTranslation } from 'react-i18next'
import { useLocation } from 'react-router-dom'
import { trackLoginCompleted } from '../../utils/analytics'
import { saveLpSession, readLpSession } from '../../utils/session'
import { useGrowthBookValues } from '../../contexts/GrowthBookContext'
import OtpStep from './otpStep'

const Step15 = ({ onSwitchToSignup, onSwitchToForgot, onSuccessContinue = null }) => {
  const location = useLocation()
  const isQuiz = !!location.pathname.includes('/quiz')
  const { magicLink, paywallNavType, paywallFlow, paywallOrder } = useGrowthBookValues()
  const isMagicLink = magicLink === 'ON'
  // GrowthBook flags forwarded with auth calls so the backend can attribute
  // conversions per A/B variant (this lander's magic-link flag is white-specific).
  // All three nav-config fields ride along, matching createAccount.jsx / Step5.jsx.
  const ab_testing = {
    'createsexfriends-white-magicLink': magicLink,
    'createsexfriends-white-paywall-nav-config': paywallNavType,
    'createsexfriends-white-paywall-flow': paywallFlow,
    'createsexfriends-white-paywall-order': paywallOrder,
  }
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [otpState, setOtpState] = useState(null)
  const [selections] = useQuizSelections() // Get quiz selections
  const googleBtnRef = useRef(null)

  // Returning visitor: the stored LP session knows the email they registered
  // with (utils/session.js), so prefill it rather than making them retype it.
  const storedSessionRef = useRef(readLpSession())

  // Use the custom validation hook
  const { values, validateAll, getFieldProps } = useFormValidation(
    { email: storedSessionRef.current?.email || '', password: '' },
    {
      email: validationRules.email,
      password: validationRules.password,
    },
  )

  // useEffect(() => {
  //   const setupGoogleAuth = async () => {
  //     // Collect UTM data
  //     const utmObj = collectAndStoreUTMData();

  //     if (googleBtnRef.current) {
  //       try {
  //         const googleAuthUrl = await getGoogleAuthUrl(selections);
  //         googleBtnRef.current.href = googleAuthUrl;
  //       } catch (error) {
  //         const basicGoogleAuthUrl = await getGoogleAuthUrl();
  //         googleBtnRef.current.href = basicGoogleAuthUrl;
  //       }
  //     }
  //   };

  //   setupGoogleAuth();
  // }, [selections]);
  const { t } = useTranslation()
  useEffect(() => {
    const utmObj = collectAndStoreUTMData()
    if (!googleBtnRef.current) return

    if (isQuiz) {
      // /quiz: defer Google OAuth until the final step (same as signup) — keeps user on
      // the prelander host through the quiz, OAuth fires with full model-params at the end.
      googleBtnRef.current.href = '#'
      googleBtnRef.current.onclick = e => {
        e.preventDefault()
        localStorage.removeItem('quizAuthToken')
        localStorage.removeItem('authToken')
        localStorage.setItem('pendingGoogleSignup', 'true')
        localStorage.setItem('pendingRegistrationSource', 'google')
        sessionStorage.setItem('quizSessionAuthed', 'true')
        console.log('[quiz] Google deferred — pendingGoogleSignup set, advancing quiz')
        if (onSuccessContinue) {
          onSuccessContinue({ authToken: null })
        }
      }
      return
    }

    // Mirrors the sibling create-friend lander's Google OAuth state assembly:
    // utm + ef_transaction_id (conditional spread) + externalClickIds at the top level of
    // state, then JSON.stringify into the `?state=` query. Encode protection=sfw into `path`
    // so it ends up on the post-OAuth paywall URL — backend builds the final URL via
    // `new URL(BASE + path)`, which parses the embedded query string correctly.
    const setupGoogleAuth = async () => {
      const efTransactionId = getEfTransactionId()
      const externalClickIds = await getExternalClickIds()
      if (!googleBtnRef.current) return
      const finalState = {
        ...utmObj,
        lp_source: LP_SOURCE,
        ...(efTransactionId ? { ef_transaction_id: efTransactionId } : {}),
        externalClickIds,
        // chat_paywall → /lobby?protection=sfw; else /paywall?protection=sfw.
        path: buildOAuthStatePath(paywallNavType),
      }
      // chat_paywall must still carry the model slug into the chat — backend reads model-params from state.
      if (paywallNavType === 'chat_paywall' && selections) {
        try {
          finalState['model-params'] = await createModelParams(selections)
          finalState.type = selections.welcome === 'Anime' ? 'anime' : 'realistic'
        } catch (err) {
          console.error('Error creating model params for Google auth', err)
        }
      }
      const googleAuthUrl = getGoogleAuthUrl()
      const oauthUrl = `${googleAuthUrl}?state=${encodeURIComponent(JSON.stringify(finalState))}`
      googleBtnRef.current.href = oauthUrl
      googleBtnRef.current.onclick = e => {
        e.preventDefault()
        localStorage.setItem('googleAuthInProgress', 'true')
        localStorage.setItem('googleAuthStartTime', Date.now().toString())
        window.location.assign(oauthUrl)
      }
    }
    setupGoogleAuth()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isQuiz, paywallNavType, selections])

  useEffect(() => {
    // /quiz handles its own Google auth callback in QuizFlow.jsx (keeps user in the quiz)
    if (isQuiz) return
    handleAuthCallback(selections)
  }, [selections, isQuiz])

  const handleEmailOnlySubmit = async () => {
    const emailErr = validationRules.email(values.email)
    if (emailErr) {
      setError(emailErr)
      return
    }
    setIsLoading(true)
    setError('')
    try {
      const data = await submitEmailOnly(values.email, { selections, ab_testing })

      if (data?.success === false) {
        setError(data.message || t('please_check_email'))
        return
      }

      if (data?.otp_sent) {
        setOtpState({ email: values.email, otpExpiry: data.otpExpiry })
        return
      }

      if (data?.authToken && (data?.redirectUrl || data?.loginUrl)) {
        trackLoginCompleted()
        // Known account. The fresh token rides the redirect and is spent on
        // arrival, so store the email only — a visitor who comes back gets this
        // form prefilled (utils/session.js).
        saveLpSession({ email: values.email, source: 'magic_link' })

        if (onSuccessContinue) {
          localStorage.setItem('quizAuthToken', data.authToken)
          localStorage.removeItem('pendingGoogleSignup')
          sessionStorage.setItem('quizSessionAuthed', 'true')
          onSuccessContinue({ authToken: data.authToken })
          return
        }

        localStorage.setItem('isCompleted', true)
        window.location.href = data.redirectUrl || data.loginUrl
        return
      }

      setError(data?.message || t('please_check_email'))
    } catch (err) {
      setError(err?.message || t('network_error'))
    } finally {
      setIsLoading(false)
    }
  }

  const handleOtpVerified = data => {
    trackLoginCompleted()
    // Known account. The fresh token rides the redirect and is spent on
    // arrival, so store the email only — a visitor who comes back gets this
    // form prefilled (utils/session.js).
    saveLpSession({ email: otpState?.email, source: 'magic_link' })

    if (onSuccessContinue) {
      const authToken = data?.authToken || data?.token
      if (authToken) {
        localStorage.setItem('quizAuthToken', authToken)
      }
      localStorage.removeItem('pendingGoogleSignup')
      sessionStorage.setItem('quizSessionAuthed', 'true')
      onSuccessContinue({ authToken })
      return
    }

    localStorage.setItem('isCompleted', true)
    if (data?.redirectUrl || data?.loginUrl) {
      window.location.href = data.redirectUrl || data.loginUrl
    }
  }

  const handleLoginSubmit = async e => {
    e.preventDefault()

    if (isMagicLink) {
      await handleEmailOnlySubmit()
      return
    }

    // Validate all fields
    if (!validateAll()) {
      return
    }

    setIsLoading(true)
    setError('')

    try {
      // Pass selections to login flow for model-params
      const result = await handleLoginFlow(values.email, values.password, selections)

      if (result.success) {
        trackLoginCompleted()
        // Known account. The fresh token rides the redirect and is spent on
        // arrival, so store the email only — a visitor who comes back gets this
        // form prefilled (utils/session.js).
        saveLpSession({ email: values.email, source: 'password' })

        if (onSuccessContinue) {
          const token = getAuthToken()
          if (token) {
            localStorage.setItem('quizAuthToken', token)
          }
          localStorage.removeItem('pendingGoogleSignup')
          sessionStorage.setItem('quizSessionAuthed', 'true')
          onSuccessContinue({ authToken: token })
          return
        }

        localStorage.setItem('isCompleted', true)
        let redirectUrl = result.redirectUrl
        // create-friend-white is the SFW lander variant — every paywall handoff carries protection=sfw.
        if (redirectUrl) {
          try {
            const u = new URL(redirectUrl)
            u.searchParams.set('protection', 'sfw')
            redirectUrl = u.toString()
          } catch {}
        }
        window.location.href = redirectUrl
      } else {
        setError(result.message || t('please_check_email'))
      }
    } catch (err) {
      setError(t('network_error'))
    } finally {
      setIsLoading(false)
    }
  }

  // Get CSS classes for input styling
  const getEmailInputClass = () => {
    const emailProps = getFieldProps('email')
    if (emailProps.hasError) return 'form-input error-input'
    return 'form-input'
  }

  const getPasswordInputClass = () => {
    const passwordProps = getFieldProps('password')
    if (passwordProps.hasError) return 'form-input error-input'
    return 'form-input'
  }

  if (otpState) {
    return (
      <QuizContent className='account-page'>
        <OtpStep
          email={otpState.email}
          selections={selections}
          ab_testing={ab_testing}
          initialOtpExpiry={otpState.otpExpiry}
          onBack={() => setOtpState(null)}
          onVerified={handleOtpVerified}
        />
      </QuizContent>
    )
  }

  return (
    <QuizContent className='account-page'>
      <div className='account-container'>
        <div className='account-form-wrapper'>
          <div className='form-header'>
            {isQuiz ? (
              <>
                <h2 className='account-title quiz-account-title'>{isMagicLink ? t('start_sexting') : t('log_in')}</h2>
              </>
            ) : (
              <>
                <h2 className='account-title'>{t('log_in')}</h2>
                <p className='account-subtitle'>
                  to <img src={LogoIcon} alt='' />
                </p>
              </>
            )}
            <button className='close-button'>×</button>
          </div>

          <form onSubmit={handleLoginSubmit} className='account-form'>
            {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
            <a ref={googleBtnRef} href='#' className='google-button'>
              <svg xmlns='http://www.w3.org/2000/svg' width='18' height='18' viewBox='0 0 18 18' fill='none'>
                <path
                  fillRule='evenodd'
                  clipRule='evenodd'
                  d='M17.64 9.20454C17.64 8.56636 17.5827 7.95272 17.4764 7.36363H9V10.845H13.8436C13.635 11.97 13.0009 12.9232 12.0477 13.5614V15.8195H14.9564C16.6582 14.2527 17.64 11.9454 17.64 9.20454Z'
                  fill='#4285F4'
                />
                <path
                  fillRule='evenodd'
                  clipRule='evenodd'
                  d='M8.99976 18C11.4298 18 13.467 17.1941 14.9561 15.8195L12.0475 13.5614C11.2416 14.1014 10.2107 14.4204 8.99976 14.4204C6.65567 14.4204 4.67158 12.8373 3.96385 10.71H0.957031V13.0418C2.43794 15.9832 5.48158 18 8.99976 18Z'
                  fill='#34A853'
                />
                <path
                  fillRule='evenodd'
                  clipRule='evenodd'
                  d='M3.96409 10.71C3.78409 10.17 3.68182 9.59318 3.68182 8.99999C3.68182 8.40681 3.78409 7.82999 3.96409 7.28999V4.95818H0.957273C0.347727 6.17318 0 7.54772 0 8.99999C0 10.4523 0.347727 11.8268 0.957273 13.0418L3.96409 10.71Z'
                  fill='#FBBC05'
                />
                <path
                  fillRule='evenodd'
                  clipRule='evenodd'
                  d='M8.99976 3.57955C10.3211 3.57955 11.5075 4.03364 12.4402 4.92545L15.0216 2.34409C13.4629 0.891818 11.4257 0 8.99976 0C5.48158 0 2.43794 2.01682 0.957031 4.95818L3.96385 7.29C4.67158 5.16273 6.65567 3.57955 8.99976 3.57955Z'
                  fill='#EA4335'
                />
              </svg>{' '}
              {t('sign_in_google')}
            </a>

            <div className='separator'>
              {isQuiz || isMagicLink ? <span>{t('or')}</span> : <span>— {t('or')} —</span>}
            </div>

            <div className='input-wrapper'>
              <input
                placeholder={t('email_label')}
                type='email'
                value={getFieldProps('email').value}
                onChange={getFieldProps('email').onChange}
                onBlur={getFieldProps('email').onBlur}
                className={getEmailInputClass()}
                aria-invalid={getFieldProps('email').hasError}
                aria-describedby={getFieldProps('email').hasError ? 'login-email-error' : undefined}
                autoComplete='email'
              />
              {getFieldProps('email').hasError && (
                <div id='login-email-error' className='error-message-input'>
                  {getFieldProps('email').error}
                </div>
              )}
            </div>
            {!isMagicLink && (
            <div className='cardStep15InputWrap'>
              <div className='input-wrapper'>
                <input
                  placeholder={t('password_label')}
                  type={showPassword ? 'text' : 'password'}
                  value={getFieldProps('password').value}
                  onChange={getFieldProps('password').onChange}
                  onBlur={getFieldProps('password').onBlur}
                  className={getPasswordInputClass()}
                  required
                  minLength={6}
                  aria-invalid={getFieldProps('password').hasError}
                  aria-describedby={getFieldProps('password').hasError ? 'login-password-error' : undefined}
                  autoComplete='current-password'
                />
                <button
                  type='button'
                  className='cardStep15InputSvg'
                  onClick={() => setShowPassword(!showPassword)}
                  style={{
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    padding: 0,
                  }}
                  aria-label={showPassword ? t('hide_password') : t('show_password')}
                >
                  {showPassword ? (
                    <svg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 16 16' fill='none'>
                      <path
                        d='M8 3.33333C11.4036 3.33333 13.6369 6.33656 14.3871 7.52455C14.4779 7.66833 14.5233 7.74023 14.5488 7.85112C14.5678 7.93439 14.5678 8.06578 14.5487 8.14905C14.5233 8.25993 14.4776 8.3323 14.3861 8.47705C14.1862 8.79343 13.8814 9.23807 13.4777 9.7203C12.7169 10.6667 11.4036 12.6667 8 12.6667C4.59644 12.6667 2.36319 9.66344 1.61292 8.47545C1.52212 8.33168 1.47672 8.25979 1.45131 8.14891C1.43221 8.06563 1.43222 7.93426 1.45133 7.85099C1.47675 7.74011 1.52236 7.66791 1.61357 7.52352C2.36369 6.33656 4.59694 3.33333 8 3.33333Z'
                        stroke='#9B9CAA'
                        strokeWidth='1.5'
                        strokeLinecap='round'
                        strokeLinejoin='round'
                      />
                      <path
                        d='M8 6C9.10457 6 10 6.89543 10 8C10 9.10457 9.10457 10 8 10C6.89543 10 6 9.10457 6 8C6 6.89543 6.89543 6 8 6Z'
                        stroke='#9B9CAA'
                        strokeWidth='1.5'
                        strokeLinecap='round'
                        strokeLinejoin='round'
                      />
                    </svg>
                  ) : (
                    <svg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 16 16' fill='none'>
                      <path
                        d='M7.16196 3.39488C7.4329 3.35482 7.7124 3.33333 8.00028 3.33333C11.4036 3.33333 13.6369 6.33656 14.3871 7.52455C14.4779 7.66833 14.5233 7.74023 14.5488 7.85112C14.5678 7.93439 14.5678 8.06578 14.5487 8.14905C14.5233 8.25993 14.4776 8.3323 14.3861 8.47705C14.1862 8.79343 13.8814 9.23807 13.4777 9.7203M4.48288 4.47669C3.0415 5.45447 2.06297 6.81292 1.61407 7.52352C1.52286 7.66791 1.47725 7.74011 1.45183 7.85099C1.43273 7.93426 1.43272 8.06563 1.45181 8.14891C1.47722 8.25979 1.52262 8.33168 1.61342 8.47545C2.36369 9.66344 4.59694 12.6667 8.00028 12.6667C9.37255 12.6667 10.5546 12.1784 11.5259 11.5177M2.00028 2L14.0003 14M6.58606 6.58579C6.22413 6.94772 6.00028 7.44772 6.00028 8C6.00028 9.10457 6.89571 10 8.00028 10C8.55256 10 9.05256 9.77614 9.41449 9.41421'
                        stroke='#9B9CAA'
                        strokeWidth='1.5'
                        strokeLinecap='round'
                        strokeLinejoin='round'
                      />
                    </svg>
                  )}
                </button>
              </div>
              {getFieldProps('password').hasError && (
                <div id='login-password-error' className='error-message-input'>
                  {getFieldProps('password').error}
                </div>
              )}
            </div>
            )}

            {error && <div className='error-message'>{error}</div>}

            {!isMagicLink && (
              <div onClick={onSwitchToForgot} className='terms-text login'>
                <span>{t('forgot_password2')}</span>
              </div>
            )}

            <button type='submit' className='submit-button' disabled={isLoading}>
              {isLoading
                ? isMagicLink
                  ? t('sending')
                  : t('logging_in')
                : isMagicLink
                  ? t('create_account2')
                  : t('log_in')}
            </button>

            {!isMagicLink && (
              <div className='login-link'>
                {t('dont_have_account')}{' '}
                <button type='button' onClick={onSwitchToSignup} className='link-button'>
                  {t('sign_up')} &gt;
                </button>
              </div>
            )}
          </form>
        </div>
      </div>
    </QuizContent>
  )
}

export default Step15
